/**
 * HTML to readable text.
 *
 * `activities.overview` is stored as HTML — the activity page renders it into
 * a prose block. Anywhere that shows it as plain text has to strip the tags
 * first, and a surface that forgets prints `<p><strong>Spa terre d'émeraude
 * </strong> is a wellness venue…` at the reader, which is what the chat's
 * pick preview shipped doing.
 *
 * Not a sanitiser and not a parser: this produces text for a `<p>`, never
 * markup for `dangerouslySetInnerHTML`. Script and style bodies go first so
 * their contents cannot survive as text, then the tags themselves, then
 * entities, then the whitespace they leave behind.
 */
const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    // A block-level close is a sentence break, not a word join: without this
    // "…therapists.</p><p>Treatments…" comes out as one run-on word.
    .replace(/<\/(p|div|li|h[1-6]|br)\s*>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&[a-z#0-9]+;/gi, (e) => ENTITIES[e.toLowerCase()] ?? " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** The first `max` characters on a word boundary, with an ellipsis if cut. */
export function truncateOnWord(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
